import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Select, Spin, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";

import {
	fetchCatalogueAppointmentTypes,
	fetchCatalogueProducts,
} from "../../../api/finance/CatalogueApi";
import { financeKeys } from "../../../api/finance/financeQueryKeys";
import { CatalogueProduct } from "../../../types/finance/CatalogueTypes";
import { InvoiceLineSourceType } from "../../../types/finance/FinanceTypes";
import { formatCents } from "../../../utils/finance/money";
import {
	CatalogueAppointmentTypeOption,
	CatalogueEmptyState,
	CatalogueVariantOption,
	ProductSearchRunner,
	buildAppointmentTypeOptions,
	buildProductOptions,
	buildVariantOptions,
	catalogueEmptyState,
	createProductSearchRunner,
	formatAvailability,
} from "./catalogueSearch";
import type { CataloguePick, InvoiceLineDraft } from "./invoiceLineDraft";
import type { UserUuid } from "../../../types/identity";

const { Text } = Typography;

/**
 * The catalogue half of one invoice line card — a product + variant pair, or an appointment type.
 *
 * ── Thin on purpose ──────────────────────────────────────────────────────────────────────────
 *
 * Every decision this widget makes about the DRAFT lives in `invoiceLineDraft.ts`
 * (`applyCataloguePick`), and every decision it makes about the OPTIONS lives in
 * `catalogueSearch.ts` (the debounced race-guarded runner, the option builders, the three empty
 * states). What is left here is binding one to the other.
 *
 * That split is not taste: **antd v5's `Select` dropdown cannot be opened under jsdom** — its
 * generated stylesheet carries a selector nwsapi rejects, which surfaces as an uncaught
 * `SyntaxError` the moment a search opens the list. Logic placed in this file could not be tested
 * at all. See `catalogueSearch.ts`'s docblock and `PatientSelect.tsx`, which this follows.
 *
 * ── Both selects are explicit (parent decision D6) ───────────────────────────────────────────
 *
 * The variant select does NOT preselect anything. Finance's projection omits `isDefaultVariant`
 * deliberately — "a default invites a silent preselection of the wrong SKU" — and a wrong SKU
 * bills the patient for the wrong thing and moves the wrong stock. There is nothing to synthesise
 * it from either: `variants[0]` is an ordering accident.
 *
 * ── Losing the catalogue must never cost the invoice ─────────────────────────────────────────
 *
 * `unavailable` is the LIKELY state on a clinic today (`access-finance` is not yet registered on
 * finance's OAuth client, so both upstream reads 403 and both endpoints degrade to an empty 200
 * carrying the flag). It renders as a line of text telling the practitioner to type the line by
 * hand — the manual fields above and below stay live throughout. Never a blocked form.
 */

export interface CatalogueLineSelectProps {
	/** The line being edited. Read for its ids, its quantity and its price — never mutated here. */
	line: InvoiceLineDraft;
	/** Which pair of controls to render. `manual` renders nothing. */
	sourceType: InvoiceLineSourceType;
	/** Decides WHICH appointment-type price is quoted — `custom_price ?? default_price` (D7). */
	practitionerId?: UserUuid;
	/** The line's index, for the `invoice-line-*-${index}` anchors the journey suite drives. */
	index: number;
	disabled?: boolean;
	onPick: (pick: CataloguePick) => void;
}

/** What an empty option list should SAY. One message per state — collapsing two of them lies. */
const emptyStateText = (state: CatalogueEmptyState | null, noun: string): string | null => {
	if (state === null) {
		return null;
	}
	if (state === "unavailable") {
		return "Catalogue unavailable — enter this line manually";
	}

	return state === "no-matches" ? `No matching ${noun}` : `No ${noun} in the catalogue`;
};

const CatalogueLineSelect: React.FC<CatalogueLineSelectProps> = ({
	line,
	sourceType,
	practitionerId,
	index,
	disabled,
	onPick,
}) => {
	const isProduct = sourceType === InvoiceLineSourceType.COMMERCE_PRODUCT;
	const isAppointmentType = sourceType === InvoiceLineSourceType.APPOINTMENT_TYPE;

	// ── Products: a debounced remote search ──────────────────────────────────────────────────
	const [products, setProducts] = useState<CatalogueProduct[]>([]);
	const [productsUnavailable, setProductsUnavailable] = useState(false);
	const [searching, setSearching] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	// ⚠️ An empty list means NOTHING until a search has come back. Classifying one before then
	// tells a practitioner their clinic sells nothing, for the ~300ms of the debounce.
	const [answered, setAnswered] = useState(false);
	// The picked product itself, so its variants are available and its label survives a later
	// search that pushes it out of the results.
	const [selectedProduct, setSelectedProduct] = useState<CatalogueProduct | null>(null);

	const runnerRef = useRef<ProductSearchRunner | null>(null);
	if (!runnerRef.current) {
		runnerRef.current = createProductSearchRunner({
			fetch: (term) => fetchCatalogueProducts({ search: term }),
			onResults: (result) => {
				setProducts(result.products);
				setProductsUnavailable(result.unavailable);
				setAnswered(true);
			},
			onSearchingChange: setSearching,
		});
	}

	useEffect(() => {
		const runner = runnerRef.current;
		return () => runner?.dispose();
	}, []);

	// A blank search is a legitimate one here — it returns the first page of the whole active
	// catalogue, which is what a freshly opened picker must show. Nobody should have to type to
	// discover their clinic sells anything.
	useEffect(() => {
		if (isProduct && products.length === 0) {
			runnerRef.current?.search("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isProduct]);

	// Re-adopt the product a SAVED line already names, once a search has returned it, so editing a
	// draft invoice can still reach that product's variants. It only ever sets LOCAL state — it
	// must never call `onPick`, which would re-run the prefill and overwrite a description and a
	// price the practitioner had already adjusted.
	useEffect(() => {
		if (selectedProduct || !line.sourceProductId) {
			return;
		}

		const match = products.find((p) => p.productId === line.sourceProductId);
		if (match) {
			setSelectedProduct(match);
		}
	}, [products, line.sourceProductId, selectedProduct]);

	// ── Appointment types: one small list, no search, keyed on the practitioner ───────────────
	const appointmentTypes = useQuery({
		queryKey: financeKeys.catalogueAppointmentTypes(practitionerId),
		queryFn: () => fetchCatalogueAppointmentTypes({ practitionerId }),
		enabled: isAppointmentType,
	});

	const productOptions = useMemo(
		() =>
			buildProductOptions(
				products,
				selectedProduct,
				line.sourceProductId ? String(line.sourceProductId) : undefined,
			),
		[products, selectedProduct, line.sourceProductId],
	);

	const variantOptions = useMemo(() => buildVariantOptions(selectedProduct), [selectedProduct]);

	const appointmentTypeOptions = useMemo(
		() => buildAppointmentTypeOptions(appointmentTypes.data?.data ?? []),
		[appointmentTypes.data],
	);

	const selectedVariant = variantOptions.find(
		(option) => option.value === String(line.sourceVariantId ?? ""),
	);
	// D2: insufficient stock WARNS and proceeds. Never disabled, never blocking — a stocktake
	// discrepancy must not stop a practitioner billing a patient.
	const short =
		selectedVariant !== undefined && Number(line.quantity ?? 0) > selectedVariant.available;

	if (!isProduct && !isAppointmentType) {
		return null;
	}

	if (isAppointmentType) {
		const message = appointmentTypes.isSuccess
			? emptyStateText(
					catalogueEmptyState(
						appointmentTypeOptions.length,
						appointmentTypes.data?.unavailable === true,
					),
					"appointment types",
				)
			: null;

		return (
			<Row gutter={[12, 8]}>
				<Col xs={24}>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Appointment type
					</Text>
					<Select
						showSearch
						filterOption={false}
						value={line.sourceAppointmentTypeId ? String(line.sourceAppointmentTypeId) : undefined}
						options={appointmentTypeOptions}
						disabled={disabled}
						placeholder="Search appointment types…"
						style={{ width: "100%" }}
						onChange={(_value, option) => {
							const picked = (option as CatalogueAppointmentTypeOption | undefined)
								?.appointmentType;
							if (picked) {
								onPick({ source: "appointment-type", appointmentType: picked });
							}
						}}
						notFoundContent={
							appointmentTypes.isFetching ? (
								<Spin size="small" />
							) : (
								<Text type="secondary" style={{ fontSize: 12 }}>
									{message ?? "No appointment types"}
								</Text>
							)
						}
						data-testid={`invoice-line-appointment-type-${index}`}
					/>
					{message && (
						<Text
							type="secondary"
							style={{ fontSize: 11 }}
							data-testid={`invoice-line-catalogue-note-${index}`}
						>
							{message}
						</Text>
					)}
				</Col>
			</Row>
		);
	}

	const productMessage = answered
		? emptyStateText(
				catalogueEmptyState(productOptions.length, productsUnavailable, searchTerm),
				"products",
			)
		: null;

	return (
		<Row gutter={[12, 8]}>
			<Col xs={24} md={12}>
				<Text type="secondary" style={{ fontSize: 12 }}>
					Product
				</Text>
				<Select
					showSearch
					filterOption={false}
					value={line.sourceProductId ? String(line.sourceProductId) : undefined}
					options={productOptions}
					disabled={disabled}
					placeholder="Search products…"
					style={{ width: "100%" }}
					onSearch={(term) => {
						setSearchTerm(term);
						runnerRef.current?.search(term);
					}}
					onChange={(_value, option) => {
						const picked = (option as { product?: CatalogueProduct } | undefined)?.product;
						if (picked) {
							setSelectedProduct(picked);
							// No variant: the practitioner picks it next, explicitly (D6).
							onPick({ source: "product", product: picked });
						}
					}}
					notFoundContent={
						searching ? (
							<Spin size="small" />
						) : (
							<Text type="secondary" style={{ fontSize: 12 }}>
								{productMessage ?? "Type to search products"}
							</Text>
						)
					}
					data-testid={`invoice-line-product-${index}`}
				/>
				{productMessage && (
					<Text
						type="secondary"
						style={{ fontSize: 11 }}
						data-testid={`invoice-line-catalogue-note-${index}`}
					>
						{productMessage}
					</Text>
				)}
			</Col>
			<Col xs={24} md={12}>
				<Text type="secondary" style={{ fontSize: 12 }}>
					Variant
				</Text>
				<Select
					showSearch
					filterOption={false}
					value={line.sourceVariantId ? String(line.sourceVariantId) : undefined}
					options={variantOptions}
					// Not "no product picked yet" — a variant chosen against no product would carry a
					// variant id with no product id, which is a provenance nothing can reconcile.
					disabled={disabled || !selectedProduct}
					placeholder={selectedProduct ? "Choose a variant" : "Choose a product first"}
					style={{ width: "100%" }}
					onChange={(_value, option) => {
						const picked = (option as CatalogueVariantOption | undefined)?.variant;
						if (picked && selectedProduct) {
							onPick({ source: "product", product: selectedProduct, variant: picked });
						}
					}}
					notFoundContent={
						<Text type="secondary" style={{ fontSize: 12 }}>
							No variants
						</Text>
					}
					data-testid={`invoice-line-variant-${index}`}
				/>
				{selectedVariant && (
					<Text
						type="secondary"
						style={{ fontSize: 11 }}
						data-testid={`invoice-line-availability-${index}`}
					>
						{formatAvailability(selectedVariant.available)} ·{" "}
						{formatCents(selectedVariant.unitPriceCents)}
					</Text>
				)}
				{short && (
					<div
						style={{ fontSize: 11, color: "var(--mg-warning, #d46b08)" }}
						data-testid={`invoice-line-stock-warning-${index}`}
					>
						Only {selectedVariant?.available} in stock — this will oversell.
					</div>
				)}
			</Col>
		</Row>
	);
};

export default CatalogueLineSelect;
